import React, { useCallback } from 'react';

import {
  type PendingDragActions,
  usePointerDrag,
} from '../../hooks/use-pointer-drag/use-pointer-drag';
import { bemBlock } from '../../modules/bem';
import { Layout } from '../layout/Layout';

import './HorizontalSplitter.less';

type SplitRateOrLeftWidthPx =
  | {
      splitRate: number;
      leftWidthPx?: undefined;
    }
  | {
      splitRate?: undefined;
      leftWidthPx: number;
    };

type HorizontalSplitterProps = {
  className?: string;
  left: React.ReactNode;
  right?: React.ReactNode;
  minSplitRate?: number;
  maxSplitRate?: number;
  minLeftPx?: number;
  minRightPx?: number;
  splitterWidthPx?: number;
  splitterHandleExtensionLeftPx?: number;
  splitterHandleExtensionRightPx?: number;
  splitterContents?: React.ReactNode;
  splitMoved(newSplitRate: number, newLeftWidthPx: number): void;
  hideLeft?: boolean;
} & SplitRateOrLeftWidthPx;

const block = bemBlock('horizontal-splitter');

export const HorizontalSplitter: React.FC<HorizontalSplitterProps> = ({
  className,
  left,
  right,
  splitRate,
  leftWidthPx,
  minSplitRate = 0,
  maxSplitRate = 1,
  minLeftPx = 0,
  minRightPx = 0,
  splitterWidthPx = 2,
  splitterHandleExtensionLeftPx = 0,
  splitterHandleExtensionRightPx = 4,
  splitterContents = null,
  splitMoved,
  hideLeft,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const sliderRef = React.useRef<HTMLDivElement | null>(null);
  const ghostRef = React.useRef<HTMLDivElement | null>(null);

  const clampedCssValue = useCallback(
    (unclamped: string): string => {
      return `clamp(
        max(${100 * minSplitRate}%, ${minLeftPx}px),
        ${unclamped},
        min(${100 * maxSplitRate}%, calc(100% - ${minRightPx + splitterWidthPx}px)))`;
    },
    [minSplitRate, maxSplitRate, minLeftPx, minRightPx, splitterWidthPx],
  );

  const startDrag = React.useCallback((): PendingDragActions | undefined => {
    const container = containerRef.current;
    const slider = sliderRef.current;
    const ghost = ghostRef.current;

    if (!container || !slider || !ghost) {
      return;
    }

    const startLeft = slider.getBoundingClientRect().left - container.getBoundingClientRect().left;
    ghost.style.left = clampedCssValue(`${startLeft}px`);
    ghost.style.display = 'block';

    return {
      move: ({ relativeX }) => {
        ghost.style.left = clampedCssValue(`${startLeft + relativeX}px`);
      },
      finish: () => {
        const newPosPx = parseFloat(getComputedStyle(ghost).left);
        const { width: containerWidth } = container.getBoundingClientRect();

        ghost.style.display = '';
        splitMoved(newPosPx / containerWidth, newPosPx);
      },
      cancel: () => {
        ghost.style.display = '';
      },
    };
  }, [clampedCssValue, splitMoved]);

  const pointerEventHandlers = usePointerDrag({
    startDrag,
    dragHandleSelector: '*',
    allowDraggingByRootElement: true,
  });

  const leftWidthCss = splitRate !== undefined ? `${100 * splitRate}%` : `${leftWidthPx}px`;

  return (
    <Layout.Row
      wrap="nowrap"
      className={block({ extra: className })}
      position="relative"
      overflow="hidden"
      elementRef={containerRef}
    >
      {left && (
        <div
          className={block('left')}
          style={{
            ...{ display: hideLeft ? 'none' : undefined },
            width: right ? clampedCssValue(leftWidthCss) : '100%',
          }}
        >
          {left}
        </div>
      )}
      {right && (
        <>
          {left && !hideLeft && (
            <div
              className={block('slider')}
              style={{ width: `${splitterWidthPx}px` }}
              {...pointerEventHandlers}
              ref={sliderRef}
            >
              {splitterContents}
              <div
                className={block('slider-handle')}
                style={{
                  left: `${-splitterHandleExtensionLeftPx}px`,
                  right: `${-splitterHandleExtensionRightPx}px`,
                }}
              />
            </div>
          )}
          <div className={block('right')}>{right}</div>
          {left && (
            <div
              ref={ghostRef}
              style={{
                width: `${
                  splitterWidthPx + splitterHandleExtensionLeftPx + splitterHandleExtensionRightPx
                }px`,
                transform: `translateX(${-splitterHandleExtensionLeftPx}px)`, // Using transform, so that it is always added to left.
              }}
              className={block('slider-ghost')}
            />
          )}
        </>
      )}
    </Layout.Row>
  );
};
